import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import useSearch from "../../../../../../../../hooks/use-search";
import Api from "../../../../../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../../../../../core/constants/enums";
import ToggleButtons from "../../../../../../../base/button/toggle_buttons";
import {Col} from "reactstrap";
import SearchInput from "../../../../../../../base/input/search_input";
import TryAgain from "../../../../../../../app-specific/try-again";
import {
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridDensities,
    DefaultDataGridPageSizes,
    useDataGridSavedState
} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {ProductTechnicalFileAssignedItemsDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import ProductInformationPopover from "../../../../../../../app-specific/draggable-popover/product-information-popover";

const dataGridSaveConfig = {
    name: 'AssignedItemsListProductsTabDataGrid',
    version: 1,
}

const AssignedItemsListProductsTab = ({technicalFileId, assign, unAssign, setInAddMode, setCategories, setLoadingCategories}) => {
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                title: 'Part Number',
                name: ProductTechnicalFileAssignedItemsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: 'Category',
                name: ProductTechnicalFileAssignedItemsDataGridColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                name: ProductTechnicalFileAssignedItemsDataGridColumnNames.toggle,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
                sortable: false,
            },
        ], [])

    const [dataGridColumns, , dataGridPageSize] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize,
        sizes: DefaultDataGridPageSizes,
    } : {
        ...DefaultPaginationInfo,
        sizes: DefaultDataGridPageSizes,
    }, [dataGridPageSize]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        filters: {},
    }), [initialPaginationInfo]);

    /**
     * Searches among the assigned products to the current technical file.
     * @type {import("../../../../../../../../hooks/use-search").UseSearchFunction}
     */
    const searchAssignedProducts = useCallback(async (args) => {
        setLoadingCategories(true);
        const forApi = {
            filters: {
                documentId: technicalFileId,
                partNo: args.filters?.keyword ?? "",
            },
            paginationInfo: args.paginationInfo,
        };
        const response = await Api.searchOperatorPartTechnicalFileAssignedParts(forApi);
        setLoadingCategories(false);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCategories(response?.data?.categories ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.paginationInfo?.length ?? 0,
            }));
            setData(response?.data?.resultList?.map(e => ({
                ...e,
                togglingAssigned: false,
                assigned: true,
            })) ?? []);
        }
    }, [technicalFileId, isMounted, setCategories, setLoadingCategories]);

    const searchData = useSearch(searchAssignedProducts, initialSearchDataSettings, false);
    const {setFilters} = searchData;

    /**
     * Updates the part-number filter and triggers a new search via useSearch.
     * @param {string} partNo
     */
    const searchByPartNo = useCallback((partNo) => {
        setFilters({keyword: partNo ?? ''});
    }, [setFilters]);

    /**
     * Assigns the given record to the current technical file.
     * @param record
     * @return {Promise<void>}
     */
    const assignProduct = async (record) => {
        setData(prevState => prevState.map(e => e.id === record.id
            ? {...e, togglingAssigned: true}
            : e
        ))
        const forApi = {
            records: [record]
        }
        const response = await assign(forApi);
        if (!isMounted()) return;
        setData(prevState => prevState.map(e => e.id === record.id
            ? {...e, togglingAssigned: false}
            : e
        ))
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === record.id
                ? {...e, assigned: true}
                : e
            ))
        }
    }

    /**
     * Un-Assigns the given record from the current technical file.
     * @param record
     * @return {Promise<void>}
     */
    const unAssignProduct = async (record) => {
        setData(prevState => prevState.map(e => e.id === record.id
            ? {...e, togglingAssigned: true}
            : e
        ))
        const forApi = {
            records: [record]
        }
        const response = await unAssign(forApi);
        if (!isMounted()) return;
        setData(prevState => prevState.map(e => e.id === record.id
            ? {...e, togglingAssigned: false}
            : e
        ))
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === record.id
                ? {...e, assigned: false}
                : e
            ))
        }
    }

    /**@type DataGridRow[]}*/
    const dataGridRows = useMemo(() => data?.map(record => ({
        key: record.id,
        data: record,
        cells: {
            [ProductTechnicalFileAssignedItemsDataGridColumnNames.partNo]:
                <ProductInformationPopover partNo={record.partNumber}>
                    {record.partNumber ?? ""}
                </ProductInformationPopover>,
            [ProductTechnicalFileAssignedItemsDataGridColumnNames.category]: record.parentCategory,
            [ProductTechnicalFileAssignedItemsDataGridColumnNames.toggle]:
                <ToggleButtons
                    className={'assigned-toggle-buttons'}
                    value={record.assigned}
                    size={'small'}
                    onChange={(_) => record.assigned ? unAssignProduct(record) : assignProduct(record)}
                    options={[{
                        value: record.assigned,
                        name: record.togglingAssigned
                            ? "toggling..."
                            : record.assigned ? "Un-assign" : "Assign",
                        disabled: record.togglingAssigned
                    }]}
                />,
        },
    })), [data])

    return (
        <>
            <Col xs={12} className='px-3'>
                <SearchInput
                    autoSearch
                    immediateSearch
                    variant={'outlined'}
                    loading={searchData.loading}
                    search={searchByPartNo}
                    initialValue={searchData.filters?.keyword ?? ''}
                    label={'Search By Part Number'}
                />
            </Col>
            <Col xs={12} className={'mt-4'}>
                {
                    !searchData.loading && !data?.length
                        ? searchData.filters?.keyword?.length
                            ? <TryAgain
                                text={'There are no assigned products with the current filters'}
                                buttonText={'Clear Filters'}
                                onClick={() => searchData.setFilters({keyword: ''})}
                                showButton={true}
                            />
                            : <TryAgain
                                text={'There are no products assigned to this document. You may assign new products' +
                                    ' if you wish'}
                                buttonText={'Assign new Products'}
                                onClick={() => setInAddMode(true)}
                                showButton={true}
                            />
                        : <PartnerDataGrid
                            {...dataGridSaveConfig}
                            hideFooterActions
                            hideExporting
                            initialLayoutState={{
                                columns: columns,
                            }}
                            classNames={{
                                toolbar: "pt-0"
                            }}
                            visibleRows={5}
                            rows={dataGridRows}
                            columns={dataGridColumns}
                            state={{
                                loading: {state: searchData.loading},
                                pagination: searchData.paginationInfo,
                                density: DataGridDensities.compact,
                            }}
                            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                            onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        />
                }
            </Col>
        </>
    );
}

export default AssignedItemsListProductsTab;
